import { Kbd, Popover, cn } from '@nexo/ui';
import {
  BadgeCheck,
  Hash,
  IterationCw,
  ListPlus,
  Menu,
  MessageSquarePlus,
  MessagesSquare,
  Plus,
  Search,
  SquareCheckBig,
  UserPlus,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';
import { LogoMark } from '../components/Logo';
import { useMe } from '../features/auth/AuthProvider';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { UserMenu } from '../features/status/UserMenu';
import { useSearchDialog } from '../features/search/SearchDialog';
import { useSprints, useTasks } from '../lib/queries';
import { useTeam } from './TeamContext';
import { setAppBadge } from '../lib/badge';
import { badgeCount, showsUnread } from '../features/notifications/channelNotify';
import { can } from '../lib/permissions';

export const lastChannelKey = (teamId: string) => `nexo.lastChannel.${teamId}`;

/** Dark application bar: brand, module tabs (Chat / Tareas / Sprints) and global actions. */
export function AppTopBar() {
  const { t } = useTranslation();
  const { team, channels, openSidebar, openDialog } = useTeam();
  const me = useMe();
  const tasks = useTasks(team.id);
  const activeSprint = useSprints(team.id).data?.some((s) => s.status === 'active');
  const openSearch = useSearchDialog();
  const location = useLocation();

  // Like Discord: the red number counts mentions and DMs; other unread messages are a dot.
  const unread = channels.reduce((sum, c) => sum + badgeCount(c), 0);
  const anyUnread = channels.some(showsUnread);
  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) Nexo` : anyUnread ? '• Nexo' : 'Nexo';
    setAppBadge(unread, anyUnread);
  }, [unread, anyUnread]);
  useEffect(
    () => () => {
      document.title = 'Nexo';
      setAppBadge(0, false);
    },
    [],
  );
  const myOpenTasks = (tasks.data ?? []).filter(
    (t) => t.status !== 'done' && t.assignees.some((a) => a.id === me.id),
  ).length;
  const lastChannel = localStorage.getItem(lastChannelKey(team.id));
  const chatTarget = channels.some((c) => c.id === lastChannel)
    ? lastChannel
    : channels.find((c) => c.kind === 'channel' && c.mode === 'text')?.id;
  const onTasks = location.pathname.endsWith('/tasks');
  const onSprints = location.pathname.endsWith('/sprints');

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 bg-ink px-3 text-ink-fg [--avatar-ring:var(--color-ink)] md:h-[3.25rem]">
      <button
        type="button"
        aria-label={t('Abrir menú')}
        onClick={openSidebar}
        className="flex size-9 items-center justify-center rounded-lg text-ink-muted hover:bg-white/10 hover:text-ink-fg md:hidden"
      >
        <Menu className="size-5" />
      </button>
      <Link to="/app/home" className="flex items-center gap-2 pr-1" aria-label={t('Inicio')}>
        <LogoMark className="size-7" />
        <span className="text-[17px] font-semibold tracking-tight max-sm:hidden">nexo</span>
      </Link>
      <span className="mx-1 h-5 w-px bg-white/15 max-sm:hidden" />
      <button
        type="button"
        onClick={() => openDialog('settings')}
        title={t('Ajustes del equipo')}
        className="flex min-w-0 items-center gap-1.5 rounded-md px-1 text-[15px] font-semibold hover:text-white max-sm:hidden"
      >
        <span className="truncate">{team.name}</span>
        {team.official && <BadgeCheck className="size-4 shrink-0 fill-accent text-ink" />}
      </button>

      <nav
        aria-label={t('Módulos')}
        className="mx-auto flex items-center gap-1 rounded-xl bg-white/[0.07] p-1"
      >
        <ModuleTab
          to={chatTarget ? `/app/t/${team.id}/c/${chatTarget}` : `/app/t/${team.id}`}
          active={!onTasks && !onSprints}
          icon={<MessagesSquare />}
          label="Chat"
          count={unread}
        />
        <ModuleTab
          to={`/app/t/${team.id}/tasks`}
          active={onTasks}
          icon={<SquareCheckBig />}
          label={t('Tareas')}
          count={myOpenTasks}
        />
        <ModuleTab
          to={`/app/t/${team.id}/sprints`}
          active={onSprints}
          icon={<IterationCw />}
          label="Sprints"
          dot={activeSprint}
        />
      </nav>

      <div className="flex items-center gap-1.5">
        <QuickCreate />
        <button
          type="button"
          onClick={openSearch}
          aria-label={t('Buscar')}
          title={t('Buscar (Ctrl K)')}
          className="flex h-9 items-center gap-2 rounded-lg px-2.5 text-ink-muted ring-1 ring-white/10 hover:bg-white/10 hover:text-ink-fg"
        >
          <Search className="size-[18px]" />
          <Kbd className="border-white/15 bg-white/5 text-ink-muted shadow-none max-lg:hidden">
            Ctrl K
          </Kbd>
        </button>
        <div className="rounded-lg ring-1 ring-white/10 [&_button]:text-ink-muted [&_button:hover]:bg-white/10 [&_button:hover]:text-ink-fg">
          <NotificationBell />
        </div>
        <UserMenu />
      </div>
    </header>
  );
}

function ModuleTab({
  to,
  active,
  icon,
  label,
  count = 0,
  dot = false,
}: {
  to: string;
  active: boolean;
  icon: ReactNode;
  label: string;
  count?: number;
  /** A small dot instead of a number (e.g. a sprint is running). */
  dot?: boolean | undefined;
}) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex h-8 items-center gap-2 rounded-lg px-3 text-[13px] font-medium transition-colors [&_svg]:size-4',
        active ? 'bg-white/[0.14] text-ink-fg' : 'text-ink-muted hover:text-ink-fg',
      )}
    >
      {icon}
      <span className="max-sm:hidden">{label}</span>
      {count > 0 && (
        <span
          className={cn(
            'rounded-md px-1.5 text-[11px] tabular-nums',
            active ? 'bg-white/15' : 'bg-white/10',
          )}
        >
          {count}
        </span>
      )}
      {dot && count === 0 && (
        <span aria-label={t('En marcha')} className="size-1.5 rounded-full bg-highlight" />
      )}
    </NavLink>
  );
}

/** Yellow "+" button: quick actions available from anywhere in the team. */
function QuickCreate() {
  const { t } = useTranslation();
  const { team, openTask, openDialog } = useTeam();
  const actions = [
    { icon: <ListPlus />, label: t('Nueva tarea'), run: () => openTask({ mode: 'create' }) },
    ...(can(team, 'create_channels')
      ? [{ icon: <Hash />, label: t('Nuevo canal'), run: () => openDialog('channel') }]
      : []),
    { icon: <MessageSquarePlus />, label: t('Mensaje directo'), run: () => openDialog('dm') },
    ...(can(team, 'invite_members')
      ? [{ icon: <UserPlus />, label: t('Invitar al equipo'), run: () => openDialog('members') }]
      : []),
  ];
  return (
    <Popover
      className="w-56 p-1"
      trigger={({ toggle, open }) => (
        <button
          type="button"
          onClick={toggle}
          aria-label={t('Crear')}
          aria-expanded={open}
          className="flex size-9 items-center justify-center rounded-lg bg-highlight text-highlight-fg shadow-panel transition hover:brightness-95"
        >
          <Plus className={cn('size-5 transition-transform', open && 'rotate-45')} />
        </button>
      )}
    >
      {({ close }) => (
        <ul role="menu">
          {actions.map((action) => (
            <li key={action.label}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  action.run();
                }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-surface-2 [&_svg]:size-4 [&_svg]:text-muted"
              >
                {action.icon}
                {action.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}
